import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { VersionNavigation } from "@/components/VersionNavigation";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { useUser } from "../../context/UserContext";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Helper function to get CSRF token from meta tags
const getCsrfToken = () => {
  const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
  const csrfParamTag = document.querySelector("meta[name='csrf-param']");

  if (!csrfTokenTag || !csrfParamTag) {
    console.error("CSRF token vagy param meta tag nem található!");
    return { token: null, param: null };
  }

  return {
    token: csrfTokenTag.getAttribute("content"),
    param: csrfParamTag.getAttribute("content"),
  };
};

const DictionaryTab = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [english, setEnglish] = useState("");
  const [hungarian, setHungarian] = useState("");
  const [note, setNote] = useState("");
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);
  const [editEnglish, setEditEnglish] = useState("");
  const [editHungarian, setEditHungarian] = useState("");
  const [editNote, setEditNote] = useState("");

  const { data: entries, isLoading, error } = useQuery({
    queryKey: ["dictionaryEntries"],
    queryFn: async () => {
      const response = await fetch("/api/dictionary_entries");
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    },
  });

  const createEntryMutation = useMutation({
    mutationFn: async (newEntry: { english: string; hungarian: string; note: string }) => {
      const { token } = getCsrfToken();
      if (!token) throw new Error("CSRF token not found");

      const response = await fetch("/api/dictionary_entries", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
        },
        body: JSON.stringify({ dictionary_entry: newEntry }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors.join(", "));
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dictionaryEntries"] });
      setEnglish("");
      setHungarian("");
      setNote("");
      toast({ title: "Szótárbejegyzés sikeresen létrehozva!" });
    },
    onError: (err: Error) => {
      toast({ title: "Hiba történt", description: err.message, variant: "destructive" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!english || !hungarian) {
      toast({ title: "Hiba", description: "Az Angol és Magyar mezők kitöltése kötelező.", variant: "destructive" });
      return;
    }
    createEntryMutation.mutate({ english, hungarian, note });
  };

  const handleEditClick = (entry: any) => {
    setEditingEntryId(editingEntryId === entry.id ? null : entry.id);
    setEditEnglish(entry.english || "");
    setEditHungarian(entry.hungarian || "");
    setEditNote(entry.note || "");
  };

  const handleSaveEdit = async (entryId: number) => {
    try {
      const { token } = getCsrfToken();
      if (!token) throw new Error("CSRF token not found");

      const response = await fetch(`/api/dictionary_entries/${entryId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
        },
        body: JSON.stringify({ dictionary_entry: { english: editEnglish, hungarian: editHungarian, note: editNote } }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err && err.errors) ? err.errors.join(', ') : 'A szerkesztés sikertelen.');
      }

      queryClient.invalidateQueries({ queryKey: ["dictionaryEntries"] });
      setEditingEntryId(null);
      toast({ title: "Bejegyzés frissítve" });
    } catch (err: any) {
      toast({ title: "Hiba történt", description: err.message || String(err), variant: "destructive" });
    }
  };

  const handleDeleteEntry = async (entryId: number) => {
    if (!window.confirm("Biztosan törölni szeretnéd ezt a bejegyzést?")) return;
    try {
      const { token } = getCsrfToken();
      if (!token) throw new Error("CSRF token not found");

      const response = await fetch(`/api/dictionary_entries/${entryId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": token,
        },
      });

      if (!response.ok) throw new Error("A bejegyzés törlése sikertelen.");

      queryClient.invalidateQueries({ queryKey: ["dictionaryEntries"] });
      toast({ title: "Bejegyzés törölve" });
    } catch (err: any) {
      toast({ title: "Hiba történt", description: err.message || String(err), variant: "destructive" });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Szótár</CardTitle>
        <CardDescription>Új szavak, kifejezések felvétele a szótárba.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="english">Angol</Label>
              <Input id="english" value={english} onChange={(e) => setEnglish(e.target.value)} placeholder="English term" />
            </div>
            <div>
              <Label htmlFor="hungarian">Magyar</Label>
              <Input id="hungarian" value={hungarian} onChange={(e) => setHungarian(e.target.value)} placeholder="Magyar fordítás" />
            </div>
            <div>
              <Label htmlFor="note">Megjegyzés</Label>
              <Textarea id="note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opcionális megjegyzés..." />
            </div>
          </div>
          <Button type="submit" disabled={createEntryMutation.isPending}>
            {createEntryMutation.isPending ? "Mentés..." : "Mentés a szótárba"}
          </Button>
        </form>

        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4">Szótárbejegyzések</h3>
          {isLoading ? (
            <p>Betöltés...</p>
          ) : error ? (
            <p className="text-destructive">Hiba a bejegyzések betöltése közben.</p>
          ) : entries && entries.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Angol</TableHead>
                  <TableHead>Magyar</TableHead>
                  <TableHead>Megjegyzés</TableHead>
                  <TableHead>Felhasználó</TableHead>
                  <TableHead>Akciók</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry: any) => (
                  <TableRow key={entry.id}>
                    {editingEntryId === entry.id ? (
                      <>
                        <TableCell>
                          <Input value={editEnglish} onChange={(e) => setEditEnglish(e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <Input value={editHungarian} onChange={(e) => setEditHungarian(e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <Input value={editNote} onChange={(e) => setEditNote(e.target.value)} />
                        </TableCell>
                        <TableCell>{entry.user.username}</TableCell>
                        <TableCell className="flex gap-2">
                          <Button size="sm" onClick={() => handleSaveEdit(entry.id)}>Mentés</Button>
                          <Button variant="outline" size="sm" onClick={() => setEditingEntryId(null)}>Mégse</Button>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{entry.english}</TableCell>
                        <TableCell>{entry.hungarian}</TableCell>
                        <TableCell>{entry.note}</TableCell>
                        <TableCell>{entry.user.username}</TableCell>
                        <TableCell>
                          {(user?.admin || user?.translater_admin) && (
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="icon" onClick={() => handleEditClick(entry)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="destructive" size="icon" onClick={() => handleDeleteEntry(entry.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p>Nincsenek még szótárbejegyzések.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Translate = () => {
  const { gameId, versionId } = useParams();

  const { data: gameDetails, isLoading: isLoadingGame } = useQuery({
    queryKey: ["gameDetails", gameId],
    queryFn: async () => {
      const response = await fetch(`/api/games/${gameId}`);
      if (!response.ok) {
        throw new Error("Hálózati válasz a játékhoz nem megfelelő");
      }
      return response.json();
    },
    enabled: !!gameId,
  });

  const { data: versionDetails, isLoading: isLoadingVersion } = useQuery({
    queryKey: ["versionDetails", gameId, versionId],
    queryFn: async () => {
      const response = await fetch(`/api/games/${gameId}/versions/${versionId}`);
      if (!response.ok) {
        throw new Error("Hálózati válasz a verzióhoz nem megfelelő");
      }
      return response.json();
    },
    enabled: !!gameId && !!versionId,
  });

  const isLoading = isLoadingGame || isLoadingVersion;

  if (isLoading) {
    return <div className="container mx-auto p-4">Betöltés...</div>;
  }

  return (
    <div className="container mx-auto p-4 space-y-8 bg-background text-foreground">
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/games">Játékok</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={`/games/${gameId}`}>{gameDetails?.name}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={`/games/${gameId}/versions/${versionId}`}>
                Verzió: {versionDetails?.name}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <VersionNavigation gameId={gameId} versionId={versionId} />

      <DictionaryTab />
    </div>
  );
};

export default Translate;
